import type { ReactNode } from "react";
import { Chip } from "@heroui/react";
import { cn } from "@/lib/utils";

type Tone = "profit" | "loss" | "neutral";

export function toneOf(value: number | null | undefined): Tone {
	if (value == null || !Number.isFinite(value) || value === 0) return "neutral";
	return value > 0 ? "profit" : "loss";
}

const TONE_TEXT: Record<Tone, string> = {
	profit: "text-profit",
	loss: "text-loss",
	neutral: "text-foreground",
};

interface PnlProps {
	value: number | null | undefined;
	/** Unit appended after the number, e.g. "R", "%", " USDT". */
	suffix?: string;
	digits?: number;
	/** Always show the sign (+/−). Default true. */
	signed?: boolean;
	className?: string;
}

/** A signed number coloured green/red — the only place P&L colour is decided. */
export function Pnl({ value, suffix = "", digits = 2, signed = true, className }: PnlProps) {
	if (value == null || !Number.isFinite(value)) {
		return <span className={cn("num text-muted", className)}>—</span>;
	}
	// Sign and colour follow the value as displayed, so 0.004 never shows as a green "+0.00".
	const shown = Number(value.toFixed(digits));
	const sign = signed && shown > 0 ? "+" : shown < 0 ? "−" : "";
	return (
		<span className={cn("num", TONE_TEXT[toneOf(shown)], className)}>
			{sign}
			{Math.abs(value).toFixed(digits)}
			{suffix}
		</span>
	);
}

/** LONG / SHORT pill: solid mint or red, like Higgsfield's badges, so direction reads at a glance. */
export function DirectionChip({ direction, size = "sm" }: { direction: string; size?: "sm" | "md" }) {
	const isLong = direction.toUpperCase() === "LONG" || direction.toUpperCase() === "BUY";
	return (
		<Chip
			size={size}
			variant="primary"
			className={cn(
				"num font-bold uppercase tracking-wide",
				isLong ? "bg-profit text-on-profit" : "bg-loss text-on-loss",
			)}
		>
			{isLong ? "Long" : "Short"}
		</Chip>
	);
}

interface StatTileProps {
	label: ReactNode;
	value: ReactNode;
	/** Secondary line under the value: delta, hint, sample size. */
	sub?: ReactNode;
	tone?: Tone;
	className?: string;
}

/** KPI tile: small uppercase label, large mono value (Kraken portfolio style). */
export function StatTile({ label, value, sub, tone = "neutral", className }: StatTileProps) {
	return (
		<div className={cn("rounded-lg border border-border bg-surface px-4 py-3", className)}>
			<div className="text-2xs font-medium uppercase tracking-wide text-muted">{label}</div>
			<div className={cn("num mt-1 text-xl font-semibold", TONE_TEXT[tone])}>{value}</div>
			{sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
		</div>
	);
}
